/**
 * Phase 9.7 Batch A — 🎬 Replay Post-End Modal (Save / Discard / Edit Title)
 * --------------------------------------------------------------------------
 * After the host ends a room that was recording, this modal asks them
 * what to do with the replay:
 *   • ✅ Save     → marks saved=true, clears 30-day TTL (visibility=public default)
 *   • ❌ Discard  → DELETE the replay
 *   • ✏️ Title    → quick rename
 *
 * The replay typically lands in `egress_status: 'active'` then
 * `'complete'` within ~30-120s of room end. We poll until complete.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const ReplayPostEndModal = ({ roomId, onClose }) => {
  const [replay, setReplay] = useState(null);
  const [polling, setPolling] = useState(true);
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);
  // Hard timeout — webhook normally lands within 30-120s; if it hasn't
  // resolved after 3 min we stop polling and show a soft "still processing"
  // notice so the host can come back later from their dashboard.
  const [timedOut, setTimedOut] = useState(false);

  // Poll once per 4s until the replay is `complete` or `failed`, OR until
  // we exceed the 3-minute hard ceiling.
  useEffect(() => {
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    const HARD_TIMEOUT_MS = 3 * 60 * 1000;
    const tick = async () => {
      try {
        const r = await api.get(`/live/rooms/${roomId}/replay`);
        const re = r.data?.replay || null;
        if (cancelled) return;
        if (re) {
          setReplay(re);
          if (!title) setTitle(re.title || "");
          if (re.visibility) setVisibility(re.visibility);
          if (re.egress_status === "complete" || re.egress_status === "failed") {
            setPolling(false);
            return;
          }
        }
      } catch (_e) { /* silent */ }
      if (Date.now() - startedAt > HARD_TIMEOUT_MS) {
        setTimedOut(true);
        setPolling(false);
        return;
      }
      timer = setTimeout(tick, 4000);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  if (!replay) {
    return (
      <div className="fixed inset-0 z-[400] bg-black/70 flex items-center justify-center p-4" data-testid="replay-modal-checking">
        <Card className="p-6 max-w-sm w-full bg-black/90 border-pink-500/40 text-center text-white">
          <p className="text-sm">Checking for a replay…</p>
          <Button className="mt-4" onClick={onClose} variant="outline" data-testid="replay-modal-close-checking">
            Close
          </Button>
        </Card>
      </div>
    );
  }

  const status = replay.egress_status;
  const processing = polling || status === "starting" || status === "active";
  const failed = status === "failed";

  const handleSave = async () => {
    setBusy(true);
    try {
      await api.post(`/replays/${replay.replay_id}/save`, {
        title: title.trim() || replay.title,
        visibility,
      });
      toast.success("Replay saved 🎉");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save replay");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm("Discard this replay forever?")) return;
    setBusy(true);
    try {
      await api.delete(`/replays/${replay.replay_id}`);
      toast("Replay discarded.");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't discard");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[400] bg-black/75 flex items-center justify-center p-4" data-testid="replay-post-end-modal">
      <Card className="p-6 max-w-md w-full bg-gradient-to-br from-rose-900/40 via-black to-pink-900/30 border-pink-500/40 text-white">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🎬</span>
          <h2 className="text-xl font-black">Your Replay</h2>
        </div>

        {processing && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-xs" data-testid="replay-modal-processing">
            ⏳ Processing — this usually takes 30–120 seconds. You can stay
            on this screen or come back later from your dashboard.
          </div>
        )}

        {timedOut && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-100 text-xs" data-testid="replay-modal-timed-out">
            ⏳ Still processing. You can close this and finish from your dashboard
            once it's ready — it usually wraps up within a few more minutes.
          </div>
        )}

        {failed && (
          <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-xs" data-testid="replay-modal-failed">
            😔 Recording failed during processing. No replay was created —
            you weren't charged any quota minutes.
          </div>
        )}

        <div className="mb-4 space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-pink-200/70 font-bold">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled replay"
              maxLength={120}
              className="bg-black/40 border-pink-500/40 text-white"
              data-testid="replay-modal-title-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-pink-200/70 font-bold">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="w-full mt-1 bg-black/40 border border-pink-500/40 text-white rounded-md px-3 py-2 text-sm"
              data-testid="replay-modal-visibility-select"
            >
              <option value="public">Public (anyone on RGM)</option>
              <option value="unlisted">Unlisted (link only)</option>
              <option value="private">Private (just me)</option>
            </select>
          </div>

          <div className="text-[11px] text-pink-200/70">
            Duration · <span className="text-white font-bold">{Math.round((replay.duration_seconds || 0) / 60)} min</span>
            {" · "}
            Format · <span className="text-white font-bold">{(replay.media_kind || "audio").toUpperCase()}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSave}
            disabled={busy || failed || processing}
            className="flex-1 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold"
            data-testid="replay-modal-save-btn"
          >
            {busy ? "Saving…" : "✅ Save Replay"}
          </Button>
          <Button
            onClick={handleDiscard}
            disabled={busy}
            variant="outline"
            className="border-pink-500/50 text-pink-100 hover:bg-pink-500/15"
            data-testid="replay-modal-discard-btn"
          >
            🗑 Discard
          </Button>
        </div>

        <button
          onClick={onClose}
          className="mt-4 text-xs text-pink-200/60 hover:text-white"
          data-testid="replay-modal-skip"
        >
          Skip for now (decide later from dashboard)
        </button>
      </Card>
    </div>
  );
};

export default ReplayPostEndModal;
